import useLanguageNavigate from '@hooks/useLanguageNavigate';
import BookIcon from '@mui/icons-material/Book';
import ChatIcon from '@mui/icons-material/Chat';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CommentIcon from '@mui/icons-material/Comment';
import CloseIcon from '@mui/icons-material/Close';
import { Avatar, Box, Button, Typography, Badge, Modal, IconButton, keyframes } from '@mui/material';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { acceptOffer, markCommentAsRead } from '../services/services';
import { Offer } from '../types';
import 'react-quill/dist/quill.snow.css';

const flagCDN = 'https://flagcdn.com/w160/';

// Define the pulse animation
const pulseAnimation = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  }
  
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 5px rgba(244, 67, 54, 0);
  }
  
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
`;

interface OfferCardProps {
	offer: Offer;
	onClose?: () => void;
	onPay?: (offerId: string, requestId?: string) => void;
	onChat?: (offerId: string, requestId?: string) => void;
	onFieldbook?: (fieldbookUrl: string) => void;
	isMobile?: boolean;
	isCompleted?: boolean;
	isArchived?: boolean;
	chatLoading?: boolean;
	onRefresh?: () => void; // Add callback to refresh offers data
}

// Comment Modal Component
const CommentModal = ({ open, handleClose, comment }: { open: boolean; handleClose: () => void; comment: string }) => {
	const { t } = useTranslation();
	
	return (
		<Modal
			open={open}
			onClose={handleClose}
			aria-labelledby="comment-modal-title"
			aria-describedby="comment-modal-description"
		>
			<Box sx={{
				position: 'absolute',
				top: '50%',
				left: '50%',
				transform: 'translate(-50%, -50%)',
				width: { xs: '90%', sm: 500 },
				bgcolor: 'background.paper',
				boxShadow: 24,
				p: 4,
				borderRadius: 2,
				maxHeight: '80vh',
				overflow: 'auto'
			}}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
					<Typography id="comment-modal-title" variant="h6" component="h2">
						{t('comment')}
					</Typography>
					<IconButton onClick={handleClose} sx={{ color: 'gray' }}>
						<CloseIcon />
					</IconButton>
				</Box>
				<Box 
					sx={{
						'& ul': {
							paddingLeft: '15px',
							listStyleType: 'disc',
							margin: '4px 0',
						},
						'& ol': {
							paddingLeft: '15px',
							listStyleType: 'decimal',
							margin: '4px 0',
						},
						'& li': {
							display: 'list-item',
							margin: '2px 0',
						},
						'& a': {
							color: '#3da08f',
							textDecoration: 'underline',
						},
						'& p': {
							margin: '4px 0',
						},
					}}
					dangerouslySetInnerHTML={{ __html: comment || t('noComment') }} 
				/>
			</Box>
		</Modal>
	);
};

export const OfferCard: React.FC<OfferCardProps> = ({
	offer,
	onClose,
	onPay,
	onChat,
	onFieldbook,
	isMobile,
	isCompleted = false,
	isArchived = false,
	chatLoading = false,
	onRefresh,
}) => {
	const { t } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();
	const [paymentLoading, setPaymentLoading] = useState(false);
	const [commentModalOpen, setCommentModalOpen] = useState(false);
	const [markingAsRead, setMarkingAsRead] = useState(false);
	const [localCommentSeen, setLocalCommentSeen] = useState(false);
	const [isLocalAccepted, setIsLocalAccepted] = useState(false); // Add local state to track if offer was accepted
	
	const {
		id,
		specialist,
		service_delivery_days,
		price,
		status,
		reference_id,
		is_paid,
		active_for_days,
		actions_available = [],
		service,
		end_date,
		offer_comment,
		comment_count = 0,
		farmer_seen_offer_comment = true,
		is_free,
	} = offer;

	// Handle price conversion - API returns string, component expects number
	const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

	// Handle service delivery days - API has service.duration, fallback to service_delivery_days
	const deliveryDays =
		service_delivery_days || (service?.duration ? parseInt(service.duration) : 0);
		
	// Calculate days remaining based on end_date or fall back to active_for_days
	const timeRemaining = useMemo(() => {
		if (end_date) {
			try {
				// For ISO format like "2025-07-25T13:15:08.000Z"
				const endDateObj = new Date(end_date);
				
				if (endDateObj instanceof Date && !isNaN(endDateObj.getTime())) {
					const currentDate = new Date();
					console.log('End date from API:', endDateObj);
					console.log('Current date:', currentDate);
					
					// Calculate time difference
					const diffTime = endDateObj.getTime() - currentDate.getTime();
					console.log('Diff time (ms):', diffTime);
					
					// Convert to hours and days
					const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
					const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
					console.log('Hours remaining:', diffHours);
					console.log('Days remaining:', diffDays);
					
					// If time is in the future
					if (diffTime > 0) {
						// If less than 1 day remains, show hours
						if (diffTime < 24 * 60 * 60 * 1000) {
							console.log('Using hours:', diffHours);
							return { value: diffHours, unit: 'hours' };
						} else {
							console.log('Using days:', diffDays);
							return { value: diffDays, unit: 'days' };
						}
					} else {
						// If time has expired, show 0 days
						console.log('Time expired, showing 0 days');
						return { value: 0, unit: 'days' };
					}
				} else {
					console.error('Invalid date object created from end_date:', end_date);
				}
			} catch (error) {
				console.error('Error calculating time remaining:', error);
			}
		}
		
		// Fallback to active_for_days or default values
		const days = is_paid 
			? (active_for_days || deliveryDays || 7) 
			: (active_for_days || 3);
		console.log('Using fallback days:', days);
		return { value: days, unit: 'days' };
	}, [end_date, active_for_days, is_paid, deliveryDays]);

	const handlePayment = useCallback(async (offerId: string) => {
		// If already accepted locally, don't try to accept again
		if (isLocalAccepted) {
			console.log('Offer already accepted locally, skipping payment call');
			return;
		}
		
		// Set a timeout to prevent infinite loading
		const loadingTimeout = setTimeout(() => {
			console.log('Payment timeout reached, resetting loading state');
			setPaymentLoading(false);
		}, 10000); // 10 seconds timeout
		
		setPaymentLoading(true);
		try {
			console.log(`Handling payment/acceptance for offer: ${offerId}, is_free: ${is_free}`);
			const response = await acceptOffer(offerId);
			
			// Clear the timeout since we got a response
			clearTimeout(loadingTimeout);
			
			console.log('Offer acceptance response:', response);
			
			if (response.success) {
				// If it's a paid offer with a redirect URL, navigate to payment page
				if (response.data && response.data.redirect_url) {
					console.log('Redirecting to payment page:', response.data.redirect_url);
					window.location.href = response.data.redirect_url;
				} else {
					// For free offers, mark as accepted locally and refresh
					console.log('Free offer accepted successfully');
					setIsLocalAccepted(true);
					
					// Refresh offers list if callback is provided
					if (onRefresh) {
						console.log('Refreshing both requests and offers lists');
						onRefresh();
					}
				}
			} else {
				console.error('Offer acceptance failed:', response.error);
				alert(t('offerAcceptanceFailed', 'Failed to accept offer. Please try again.'));
			}
		} catch (error) {
			// Clear the timeout in case of error
			clearTimeout(loadingTimeout);
			console.error('Payment processing error:', error);
			alert(t('offerAcceptanceFailed', 'Failed to accept offer. Please try again.'));
		} finally {
			// Ensure loading state is reset
			setPaymentLoading(false);
		}
	}, [onRefresh, is_free, t, isLocalAccepted]);

	// Reset loading state when component unmounts
	useEffect(() => {
		return () => {
			setPaymentLoading(false);
		};
	}, []);

	const handlePay = (e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent triggering parent click events
		handlePayment(id);
	};

	const handleChat = (e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent triggering parent click events
		if (onChat && !isCompleted && !isArchived) onChat(id, specialist.id);
	};

	const handleCommentClick = (e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent triggering parent click events
		
		// Start API call first if needed, but don't wait for it
		if (comment_count > 0 && !farmer_seen_offer_comment && !localCommentSeen && !markingAsRead) {
			setMarkingAsRead(true);
			
			// Update local state immediately for better UX
			setLocalCommentSeen(true);
			
			// Make API call in the background
			markCommentAsRead(id)
				.catch((error) => {
					console.error('Error marking comment as read:', error);
					// If API call fails, revert the local state
					setLocalCommentSeen(false);
				})
				.finally(() => {
					setMarkingAsRead(false);
				});
		}
		
		// Open the modal immediately after starting the API call
		// This happens synchronously, so the modal opens right away
		setTimeout(() => {
			setCommentModalOpen(true);
		}, 0);
	};
	
	// Handle modal close and refresh data if needed
	const handleCloseModal = () => {
		setCommentModalOpen(false);
		
		// Only refresh data when explicitly requested (e.g., on page navigation or manual refresh)
		// We've already updated the local state, so no need to refresh immediately
	};

	// Get status color classes
	const getStatusColorClasses = () => {
		if (isArchived) return 'text-[#343A40] border-[#E9ECEF] bg-[#F8F9FA]';
		if (isCompleted || is_paid) return 'text-[#00AB89] border-[#00AB89] bg-[#EBFDF9]';
		if (status === 'ongoing') return 'text-[#FFAA2B] border-[#FFAA2B] bg-[#FFF7EA]';
		return 'text-[#343A40] border-[#E9ECEF] bg-[#F8F9FA]';
	};

	const handleProfileClick = (e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent triggering parent click events
		navigateWithLanguage(`/farmer/specialist-profile/${specialist.id}`);
	};

	return (
		<div className="bg-white rounded-[10px] shadow-custom-button mb-4 relative border border-[#E9ECEF]">
			<div>
				{/* Comment Modal */}
				<CommentModal 
					open={commentModalOpen} 
					handleClose={handleCloseModal} 
					comment={offer_comment || ''} 
				/>
				
				{/* Specialist Info Header */}
				<div className="flex cursor-pointer p-5" onClick={handleProfileClick}>
					<Avatar
						src={specialist.profile_image_url}
						alt={specialist.full_name}
						sx={{
							width: isMobile ? 48 : 64,
							height: isMobile ? 48 : 64,
							borderRadius: '8px',
						}}
						className="shadow-custom-button"
					/>

					<div className="ml-4 flex-1">
						<Typography
							variant="h6"
							className="font-roboto font-bold text-[16px] text-dark leading-[140%]"
							sx={{ fontSize: isMobile ? '14px' : '16px' }}
						>
							{specialist.full_name}
						</Typography>

						{/* Country with flag */}
						<div className="flex items-center mt-1 gap-2">
							<img
								src={`${flagCDN}${specialist.country?.alpha_two?.toLowerCase() || specialist.country?.id?.toLowerCase() || 'xx'}.png`}
								alt={specialist.country?.alpha_two || specialist.country?.id || ''}
								style={{ width: isMobile ? 14 : 16, borderRadius: '2px' }}
							/>
							<span
								className="text-dark font-roboto text-base font-normal"
								style={{ fontSize: isMobile ? '12px' : '16px' }}
							>
								{specialist.country?.name || t('unknown')}
							</span>
						</div>

						{/* Service delivery days */}
						<span
							className="text-[#7B809A] font-roboto text-sm font-normal mt-1"
							style={{ fontSize: isMobile ? '11px' : '14px' }}
						>
							{t('serviceDeliveryDays', { days: deliveryDays })}
						</span>
					</div>

					{/* Price Display */}
					<div
						className={`rounded-[8px] border font-roboto text-[16px] font-[600] leading-normal flex items-center justify-center relative ${getStatusColorClasses()}`}
						style={{
							width: isMobile ? '48px' : '64px',
							height: isMobile ? '48px' : '64px',
							fontSize: isMobile ? '14px' : '16px',
						}}
					>
						{(is_paid || isCompleted) && !isArchived && (
							<CheckCircleIcon
								sx={{
									color: '#00AB89',
									fontSize: isMobile ? '18px' : '24px',
								}}
								className="absolute top-[-10px] right-[-10px] text-white fill-[#00AB89] rounded-[8px] p-[2px] bg-white"
							/>
						)}
						€{numericPrice}
					</div>
				</div>

				{/* Status Bar - Hidden for archived offers */}
				{!isArchived && (
					<div className="flex justify-between items-center mb-3 py-[5px] px-[10px] bg-disabled border-t border-b border-[#E9ECEF]">
						<p
							className={`font-roboto text-sm font-normal leading-[140%] ${isCompleted ? 'text-[#00AB89]' : 'text-[#7B809A]'}`}
							style={{ fontSize: isMobile ? '11px' : '14px' }}
						>
							{isCompleted
								? t('completed', 'Completed')
								: is_paid
									? timeRemaining.unit === 'hours'
										? `Chat closes in ${timeRemaining.value} hours`
										: `Chat closes in ${timeRemaining.value} days`
									: `Request active for ${timeRemaining.value} days`}
						</p>

						{!isMobile && (
							<div className="flex items-center gap-2">
								<Button
									startIcon={<BookIcon />}
									sx={{
										fontFamily: 'Roboto',
										fontSize: '16px',
										fontWeight: 700,
										padding: 0,
										textTransform: 'capitalize',
										textDecoration: 'underline',
									}}
									variant="text"
									disabled
								>
									{t('fieldbook')}
								</Button>
								
								{/* Comment Badge - replaces F-100 text */}
								<Button
									sx={{
										fontFamily: 'Roboto',
										fontSize: '16px',
										fontWeight: 700,
										padding: '0 8px',
										textTransform: 'capitalize',
										textDecoration: 'underline',
										color: '#3da08f',
										position: 'relative',
									}}
									variant="text"
									onClick={handleCommentClick}
								>
									{t('comment')}
									{comment_count > 0 && (
										<Box
											sx={{
												position: 'absolute',
												top: '-8px',
												right: '-8px',
												width: '20px',
												height: '20px',
												borderRadius: '50%',
												backgroundColor: (!farmer_seen_offer_comment && !localCommentSeen) ? '#f44336' : '#3da08f',
												color: 'white',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												fontSize: '12px',
												fontWeight: 'bold',
												animation: (!farmer_seen_offer_comment && !localCommentSeen) ? `${pulseAnimation} 2s infinite` : 'none',
											}}
										>
											{comment_count}
										</Box>
									)}
								</Button>
							</div>
						)}
					</div>
				)}

				{/* Action Buttons - Modified for archived */}
				<div className="flex justify-between items-center w-full px-3 pb-3">
					<div className="flex items-center space-x-2">
						<Button
							variant="text"
							onClick={handleChat}
							loading={chatLoading}
							disabled={isCompleted || isArchived}
							sx={{
								minWidth: isMobile ? '40px' : '68px',
								textDecoration: 'underline',
								fontSize: isMobile ? '12px' : '14px',
								opacity: isCompleted || isArchived ? 0.5 : 1,
								padding: '6px 4px',
							}}
							startIcon={<ChatIcon sx={{ fontSize: isMobile ? '16px' : '24px' }} />}
						>
							{t('chat')}
						</Button>

						{/* Comment Button for Mobile */}
						{isMobile && (
							<Button
								sx={{
									fontFamily: 'Roboto',
									fontSize: '14px',
									fontWeight: 700,
									padding: '6px 12px',
									textTransform: 'capitalize',
									color: '#3da08f',
									position: 'relative',
									textDecoration: 'underline',
								}}
								variant="text"
								onClick={handleCommentClick}
							>
								{t('comment')}
								{comment_count > 0 && (
									<Box
										sx={{
											position: 'absolute',
											top: '-5px',
											right: '-5px',
											width: '20px',
											height: '20px',
											borderRadius: '50%',
											backgroundColor: (!farmer_seen_offer_comment && !localCommentSeen) ? '#f44336' : '#3da08f',
											color: 'white',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											fontSize: '12px',
											fontWeight: 'bold',
											animation: (!farmer_seen_offer_comment && !localCommentSeen) ? `${pulseAnimation} 2s infinite` : 'none',
										}}
									>
										{comment_count}
									</Box>
								)}
							</Button>
						)}
					</div>

					<div className="flex items-center">
						{isArchived ? (
							<div className="flex h-[36px] items-center">
								{/* Empty div to maintain layout but not show anything */}
							</div>
						) : isCompleted || is_paid ? (
							<div className="flex items-center">
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: '8px',
									}}
								>
									<Box
										sx={{
											width: '28px',
											height: '28px',
											borderRadius: '50%',
											backgroundColor: '#3DA08F',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
										}}
									>
										<CheckIcon sx={{ color: 'white', fontSize: '18px' }} />
									</Box>
									<Typography
										sx={{
											fontSize: isMobile ? '14px' : '16px',
											fontWeight: 'bold',
											color: '#3DA08F',
											textTransform: 'uppercase',
										}}
									>
										{is_free ? t('accepted', 'ACCEPTED') : t('paid', 'PAID')}
									</Typography>
								</Box>
							</div>
						) : (
							<Button
								variant="contained"
								onClick={handlePay}
								loading={paymentLoading}
								disabled={paymentLoading || is_paid || isLocalAccepted}
								sx={{
									backgroundColor: isLocalAccepted ? '#00AB89' : '#3da08f',
									textTransform: 'none',
									minWidth: isMobile ? '100px' : '132px',
									fontSize: isMobile ? '12px' : '14px',
									padding: isMobile ? '4px 12px' : '6px 16px',
									borderRadius: '6px',
									'&:hover': {
										backgroundColor: isLocalAccepted ? '#00AB89' : '#2c8678',
									},
								}}
							>
								{is_paid || isLocalAccepted ? t('accepted', 'Accepted') : (is_free ? t('accept') : t('pay'))}
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
